import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStudentSubjectsById } from '@app/services/student';
import { getSessionNotesByStudent } from '@app/services/sessionNotes';
import StudentDetailsCard from '../components/StudentDetailsCard';

const HOMEWORK_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'completed', label: 'Completed' },
    { value: 'completed_and_marked', label: 'Completed & Marked' },
    { value: 'not_completed', label: 'Not Completed' },
    { value: 'na', label: 'N/A' },
];

const HOMEWORK_BADGE: any = {
    completed: 'badge-info',
    completed_and_marked: 'badge-success',
    not_completed: 'badge-danger',
    na: 'badge-secondary'
};

const HOMEWORK_LABEL: any = {
    completed: 'Completed',
    completed_and_marked: 'Completed & Marked',
    not_completed: 'Not Completed',
    na: 'N/A'
};

const SessionNotes = () => {

    const canAccess = useSelector((state: any) => state.auth.permissions['student-work']);
    const navigate = useNavigate();
    const [t] = useTranslation();
    
    useEffect(() => {
        if (canAccess?.['session-notes'] == false) {
            toast.error(t('auth.noAccess'));
            navigate('/');
        }
    }, []);

    const { id } = useSelector((state: any) => state?.auth?.selectedStudent);

    const [isLoading, setLoading] = useState(false);
    const [sessionNotes, setSessionNotes] = useState<any[]>([]);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortField, setSortField] = useState('session_date');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    const [search, setSearch] = useState({
        subjectId: '',
        month_year: '',
        homeworkStatus: ''
    });

    useEffect(() => {
        fetchSubjects();
        fetchSessionNotes(1, search);
    }, []);

    const fetchSubjects = async () => {
        try {
            const resp = await getStudentSubjectsById(id);
            if (resp.success === true) setSubjects(resp?.collections || []);
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const buildParams = (page: number, s: typeof search, sf = sortField, sd = sortDirection, pp = perPage) => {
        const params: any = { page, perPage: pp, sortField: sf, sortDirection: sd };
        if (s.subjectId) params.subjectId = s.subjectId;
        if (s.month_year) {
            const year = parseInt(s.month_year.split('-')[0]);
            if (!isNaN(year) && year.toString().length === 4) {
                params.fromDate = `${s.month_year}-01`;
                const [y, m] = s.month_year.split('-').map(Number);
                params.toDate = `${s.month_year}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
            }
        }
        if (s.homeworkStatus) params.homeworkStatus = s.homeworkStatus;
        return params;
    };

    const fetchSessionNotes = async (page: number, s: typeof search, sf = sortField, sd = sortDirection, pp = perPage) => {
        try {
            setLoading(true);
            const resp = await getSessionNotesByStudent(id, buildParams(page, s, sf, sd, pp));
            if (resp.success === true) {
                setSessionNotes(resp.collections?.data || []);
                setTotalRows(resp.collections?.pagination?.total || 0);
            } else {
                toast.error(resp?.message || 'Failed to fetch session notes');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field: string, value: string) => {
        if (field === 'month_year' && value) {
            const year = parseInt(value.split('-')[0]);
            if (isNaN(year) || year.toString().length !== 4) {
                setMonthYearError('Please enter a valid year');
                setSearch(prev => ({ ...prev, month_year: value }));
                return;
            }
        }
        if (field === 'month_year') setMonthYearError('');
        const updated = { ...search, [field]: value };
        setSearch(updated);
        setCurrentPage(1);
        fetchSessionNotes(1, updated, sortField, sortDirection);
    };

    const handleReset = () => {
        const cleared = { subjectId: '', month_year: '', homeworkStatus: '' };
        setSearch(cleared);
        setMonthYearError('');
        setCurrentPage(1);
        fetchSessionNotes(1, cleared, sortField, sortDirection);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchSessionNotes(page, search, sortField, sortDirection);
    };

    const handlePerPageChange = (newPerPage: number) => {
        setPerPage(newPerPage);
        setCurrentPage(1);
        fetchSessionNotes(1, search, sortField, sortDirection, newPerPage);
    };

    const handleSort = (field: string) => {
        const newDir = sortField === field && sortDirection === 'ASC' ? 'DESC' : 'ASC';
        setSortField(field);
        setSortDirection(newDir);
        setCurrentPage(1);
        fetchSessionNotes(1, search, field, newDir);
    };

    const [monthYearError, setMonthYearError] = useState('');
    const [hoveredCol, setHoveredCol] = useState('');

    const SortIcon = ({ field }: { field: string }) => {
        if (sortField === field) {
            return sortDirection === 'ASC'
                ? <i className="fas fa-sort-up ml-1" />
                : <i className="fas fa-sort-down ml-1" />;
        }
        if (hoveredCol === field) return <i className="fas fa-sort ml-1 text-muted" />;
        return null;
    };

    const sortableTh = (label: string, field: string, width: string) => (
        <th
            style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', width }}
            onClick={() => handleSort(field)}
            onMouseEnter={() => setHoveredCol(field)}
            onMouseLeave={() => setHoveredCol('')}
        >
            {label}
            <span style={{ display: 'inline-block', width: '16px', textAlign: 'center' }}>
                <SortIcon field={field} />
            </span>
        </th>
    );

    const totalPages = Math.ceil(totalRows / perPage);

    const getPaginationContent = () => {
        const content = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

        if (currentPage > 1) content.push(<li className="page-item" key="prev"><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}>«</a></li>);
        if (start > 1) {
            content.push(<li className="page-item" key={1}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(1); }}>1</a></li>);
            if (start > 2) content.push(<li className="page-item disabled" key="d1"><span className="page-link">...</span></li>);
        }
        for (let i = start; i <= end; i++) {
            content.push(<li className={`page-item${currentPage === i ? ' active' : ''}`} key={i}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>{i}</a></li>);
        }
        if (end < totalPages) {
            if (end < totalPages - 1) content.push(<li className="page-item disabled" key="d2"><span className="page-link">...</span></li>);
            content.push(<li className="page-item" key={totalPages}><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}>{totalPages}</a></li>);
        }
        if (currentPage < totalPages) content.push(<li className="page-item" key="next"><a className="page-link" href="#" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}>»</a></li>);
        return content;
    };

    return (
        <div>
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Session Notes</span>
                        </div>
                        <div className="col-md-12">
                            <StudentDetailsCard />
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Session Notes</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-3">
                                            <label>Subject</label>
                                            <select className="form-control" value={search.subjectId} onChange={(e) => handleFilterChange('subjectId', e.target.value)}>
                                                <option value="">All Subjects</option>
                                                {subjects.map((obj: any) => (<option key={obj.id} value={obj.id}>{obj.name}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-sm-3">
                                            <label>Month / Year</label>
                                            <input type="month" className={`form-control${monthYearError ? ' is-invalid' : ''}`} value={search.month_year} onChange={(e) => handleFilterChange('month_year', e.target.value)} />
                                            <div style={{ minHeight: '20px' }}>
                                                {monthYearError && <div className="text-danger">{monthYearError}</div>}
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <label>Homework</label>
                                            <select className="form-control" value={search.homeworkStatus} onChange={(e) => handleFilterChange('homeworkStatus', e.target.value)}>
                                                {HOMEWORK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-sm-3" style={{ marginTop: '32px' }}>
                                            <button className="btn btn-warning btn-block" onClick={handleReset}>
                                                <i className="fas fa-redo mr-1"></i> Reset
                                            </button>
                                        </div>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
                                        </div>
                                    ) : (
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '40px' }}>#</th>
                                                    {sortableTh('Session Date', 'session_date', '160px')}
                                                    {sortableTh('Subject', 'subject_name', '150px')}
                                                    {sortableTh('Homework', 'homework_status', '180px')}
                                                    <th>Session Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sessionNotes.length > 0 ? sessionNotes.map((note: any, index: number) => (
                                                    <tr key={note.id}>
                                                        <td>{(currentPage - 1) * perPage + index + 1}.</td>
                                                        <td>{moment(note.session_date).format('DD/MM/YYYY, ddd')}</td>
                                                        <td>{note.subject_name}</td>
                                                        <td><span className={`badge ${HOMEWORK_BADGE[note.homework_status] || 'badge-secondary'}`}>{HOMEWORK_LABEL[note.homework_status] || 'N/A'}</span></td>
                                                        <td>{note.session_note || '---'}</td>
                                                    </tr>
                                                )) : (
                                                    <tr><td className="text-center" colSpan={5}>No session notes to display</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {!isLoading && totalRows > 0 && (
                                    <div className="card-footer clearfix">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <span className="mr-2 text-muted">Rows per page:</span>
                                                <select
                                                    className="form-control form-control-sm"
                                                    style={{ width: 'auto' }}
                                                    value={perPage}
                                                    onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                                >
                                                    {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                                </select>
                                                <span className="ml-3 text-muted">
                                                    {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalRows)} of {totalRows}
                                                </span>
                                            </div>
                                            {totalPages > 1 && (
                                                <ul className="pagination pagination-sm m-0">
                                                    {getPaginationContent()}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SessionNotes;
